import { useState, useEffect } from 'react';

/**
 * A custom hook that provides debounced state management.
 *
 * @template T - The type of the state value
 * @param initialValue - The initial value for the state
 * @param delay - The debounce delay in milliseconds
 * @returns A tuple containing the debounced value and a setter function
 *
 * @example
 * ```tsx
 * const [searchQuery, setSearchQuery] = useDebouncedState('', 300);
 *
 * // searchQuery will be updated 300ms after the last setSearchQuery call
 * ```
 */
export function useDebouncedState<T>(value: T, delay: number): [T, React.Dispatch<React.SetStateAction<T>>] {
	const [state, setState] = useState(value);
	const [debouncedValue, setDebouncedValue] = useState(value);

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(state);
		}, delay);

		return () => {
			clearTimeout(handler);
		};
	}, [state, delay]);

	return [debouncedValue, setState];
}
